import DbService from 'resources/db.service';

import schema from './template.schema';
import { TEMPLATE_TYPES } from './template.constants';
import * as templateApi from './template.api';

const service = new DbService('templates', { schema });

const getByIds = async (companyId, ids) => {
  // let response = await service
  //   .select()
  //   .eq('company_id', companyId)
  //   .in('id', ids);

  let response = await service.selectByKey({
    selectKey: 'getTemplatesByIds',
    params: { ids },
  });

  response = service.processResponse(response);

  return response.data;
};

const createDefaultEmailTemplate = async (d) => {
  const { data } = await service.create({
    ...d,
    type: TEMPLATE_TYPES.DEFAULT,
    folder_id: d.folder_id || null,
  });

  return data[0];
};

const createTransactionEmailTemplate = async (d) => {
  const { data } = await service.create({
    ...d,
    type: TEMPLATE_TYPES.TASK,
  });

  return data[0];
};

const updateTemplate = async (data) => {
  const response = await service.updateOne(
    { id: data.id },
    data,
  );

  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

// Update the advanced field Variable in all - default and transaction - email templates
const updateFieldVariableInAllTemplates = async (companyId, oldVariable, newVariable) => {
  // Fetch templates that contain oldVariable either in body or subject

  // const response = await service
  //   .select()
  //   .eq('company_id', companyId)
  //   .or(`body.like.%${oldVariable}%,subject.like.%${oldVariable}%`);

  const response = await service.selectByKey({
    selectKey: 'updateFieldVariableInAllTemplates',
    params: { oldVariable },
  });

  const { data: templatesToUpdate } = service.processResponse(response);

  // Update each template, replacing all occurences of oldVariable with newVariable in body and subject
  const updatePromises = templatesToUpdate.map(template => {
    const updatedBody = template.body.replace(new RegExp(oldVariable, 'g'), newVariable);
    const updatedSubject = template.subject.replace(new RegExp(oldVariable, 'g'), newVariable);
    return updateTemplate({ id: template.id, body: updatedBody, subject: updatedSubject });
  });

  // Wait for all updates to complete
  const updatedTemplates = await Promise.all(updatePromises);

  return updatedTemplates;
};

// Update the advanced field Label in all - default and transaction - email templates
const updateEmailFieldLabelInAllTemplates = async (companyId, oldLabel, newLabel) => {
  // const response = await service
  //   .select()
  //   .eq('company_id', companyId)
  //   .or(`to_emails.cs.{"${oldLabel}"},cc_emails.cs.{"${oldLabel}"},bcc_emails.cs.{"${oldLabel}"}`);

  const response = await service.selectByKey({
    selectKey: 'updateEmailFieldLabelInAllTemplates',
    params: { oldLabel },
  });

  const { data: templatesToUpdate } = service.processResponse(response);
  if (!templatesToUpdate || templatesToUpdate.length === 0) {
    return [];
  }

  const updatePromises = templatesToUpdate.map((template) => {
    const updatedToEmails = template.to_emails.map(email => email === oldLabel ? newLabel : email);
    const updatedCcEmails = template.cc_emails ? template.cc_emails.map(email => email === oldLabel ? newLabel : email) : template.cc_emails;
    const updatedBccEmails = template.bcc_emails ? template.bcc_emails.map(email => email === oldLabel ? newLabel : email) : template.bcc_emails;
    return updateTemplate({ id: template.id, to_emails: updatedToEmails, cc_emails: updatedCcEmails, bcc_emails: updatedBccEmails });
  });

  // Wait for all updates to complete
  const updatedTemplates = await Promise.all(updatePromises);

  return updatedTemplates;
};

const removeFieldLabelsFromAllTemplates = async (companyId, fieldLabels) => {
  const allTemplates = await getAllTemplates(companyId);

  const isFieldLabelsInEmails = (emails, fieldLabels) =>
    emails && emails.find(email => fieldLabels.includes(email));

  const filterOutFieldLabels = (emails, fieldLabels) =>
    emails
      ? emails.filter(email => !fieldLabels.includes(email))
      : emails;

  const updatePromises = allTemplates
    .filter((template) => (
      isFieldLabelsInEmails(template.to_emails, fieldLabels) ||
      isFieldLabelsInEmails(template.cc_emails, fieldLabels) ||
      isFieldLabelsInEmails(template.bcc_emails, fieldLabels)
    ))
    .map((template) => {
      const updatedToEmails = filterOutFieldLabels(template.to_emails, fieldLabels);
      const updatedCcEmails = filterOutFieldLabels(template.cc_emails, fieldLabels);
      const updatedBccEmails = filterOutFieldLabels(template.bcc_emails, fieldLabels);

      return updateTemplate({ id: template.id, to_emails: updatedToEmails, cc_emails: updatedCcEmails, bcc_emails: updatedBccEmails });
    });

  // Wait for all updates to complete
  const updatedTemplates = await Promise.all(updatePromises);

  return updatedTemplates;

};

const removeTemplate = async (id) => {
  const response = await service.remove({ id });

  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const getDefaultTemplates = async (companyId, searchTerm) => {
  const response = await service.selectByKey({
    selectKey: 'getDefaultTemplates',
    params: { companyId, type: TEMPLATE_TYPES.DEFAULT, searchTerm },
  });

  const { data } = service.processResponse(response);

  return data || [];
};

const getAllTemplates = async (companyId) => {
  const data = await service.find({
    company_id: companyId,
  });

  return data || [];
};

const isTemplateExists = async ({ companyId, title }) => {
  // let response = await service
  //   .select('*', { head: true, count: 'exact' })
  //   .match({
  //     title: title,
  //     company_id: companyId,
  //     type: TEMPLATE_TYPES.DEFAULT,
  //   });

  const response = await service.selectByKey({
    selectKey: 'isTemplateExists',
    params: { title },
  });

  response = service.processResponse(response);

  return response?.count;
};

const getTemplatesWithoutFolderId = async (companyId, searchTerm) => {
  const response = await service.selectByKey({
    selectKey: 'getTemplatesWithoutFolderId',
    params: { searchTerm, companyId, folderId: null },
  });

  const { data } = service.processResponse(response);

  return data;
};

const deleteTemplates = async (ids) => {
  const response = await templateApi.deleteTemplates({ ids });

  return response.data;
};

export default Object.assign(service, {
  getByIds,
  createDefaultEmailTemplate,
  createTransactionEmailTemplate,
  updateTemplate,
  updateFieldVariableInAllTemplates,
  updateEmailFieldLabelInAllTemplates,
  removeFieldLabelsFromAllTemplates,
  removeTemplate,
  getDefaultTemplates,
  isTemplateExists,
  getTemplatesWithoutFolderId,
  deleteTemplates,
});
